import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { Icon } from 'semantic-ui-react';

import {
  computeHelpBubblePosition,
  getHelpBubbleWidth,
} from './hoverHelpPosition';

let nextId = 0;

/**
 * A question-mark icon with an explanation that floats over the page.
 *
 * Deliberately not a semantic-ui `Popup`: these often sit inside a Modal on a
 * phone, and Popper would not keep the bubble on a 375px screen from in there
 * (measured: it landed at x = -121 and the text was cut off by the left edge).
 * This portals to <body>, positions itself `fixed`, and clamps to the viewport
 * itself — see hoverHelpPosition.js.
 *
 * Hover opens it with a mouse; a tap opens it on touch, where there is no
 * hover to rely on. `pointerType` is what tells the two apart — relying on
 * mouseenter alone would make a tap both open and immediately close it.
 */
const HoverHelp = ({ content, label, style }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const anchorRef = useRef(null);
  const bubbleRef = useRef(null);
  const idRef = useRef(null);

  if (idRef.current === null) {
    nextId += 1;
    idRef.current = `hover-help-${nextId}`;
  }

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }
    setPosition(
      computeHelpBubblePosition({
        anchorRect: anchor.getBoundingClientRect(),
        bubbleHeight: bubbleRef.current
          ? bubbleRef.current.getBoundingClientRect().height
          : 0,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      })
    );
  }, []);

  // measure before paint, so the bubble never flashes at the wrong place
  useLayoutEffect(() => {
    if (open) {
      reposition();
    } else {
      setPosition(null);
    }
  }, [open, reposition]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const closeIfOutside = event => {
      const insideAnchor =
        anchorRef.current && anchorRef.current.contains(event.target);
      const insideBubble =
        bubbleRef.current && bubbleRef.current.contains(event.target);
      if (!insideAnchor && !insideBubble) {
        setOpen(false);
      }
    };
    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', closeIfOutside);
    document.addEventListener('touchstart', closeIfOutside);
    document.addEventListener('keydown', closeOnEscape);
    // the anchor moves with the page, so follow it rather than leaving the
    // bubble stranded
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);

    return () => {
      document.removeEventListener('mousedown', closeIfOutside);
      document.removeEventListener('touchstart', closeIfOutside);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  // Anything that is not a finger or a stylus is treated as a pointing device
  // that can hover. Checking for 'mouse' the other way round would leave hover
  // broken wherever pointerType is missing, and a browser with no pointer
  // events at all still gets the click handler below.
  const canHover = event =>
    event.pointerType !== 'touch' && event.pointerType !== 'pen';

  const bubble = open && (
    <div
      ref={bubbleRef}
      role="tooltip"
      id={idRef.current}
      style={{
        position: 'fixed',
        // until it has been measured, keep it out of sight rather than
        // letting it flash in the top-left corner
        top: position ? position.top : -9999,
        left: position ? position.left : -9999,
        // The width is settled before the first measurement rather than after
        // it: the height depends on it, and the height is what decides whether
        // the bubble has to flip above the anchor. Measuring at one width and
        // rendering at another would decide that on the wrong number.
        width: getHelpBubbleWidth(window.innerWidth),
        visibility: position ? 'visible' : 'hidden',
        zIndex: 3000,
        background: 'var(--br-surface)',
        color: 'var(--br-text)',
        border: '1px solid var(--br-border)',
        borderRadius: 4,
        boxShadow: '0 2px 8px 0 rgba(34, 36, 38, 0.2)',
        padding: '0.75em 1em',
        fontSize: '0.9rem',
        lineHeight: 1.4,
        fontWeight: 'normal',
        fontStyle: 'normal',
        textAlign: 'left',
      }}
    >
      {position && (
        <span
          // a small rotated square, so the bubble reads as attached to the
          // icon even after being pushed away from it
          style={{
            position: 'absolute',
            left: position.caretLeft - 5,
            [position.placement === 'above' ? 'bottom' : 'top']: -6,
            width: 10,
            height: 10,
            background: 'var(--br-surface)',
            borderLeft: '1px solid var(--br-border)',
            borderTop: '1px solid var(--br-border)',
            transform:
              position.placement === 'above'
                ? 'rotate(225deg)'
                : 'rotate(45deg)',
          }}
        />
      )}
      {content}
    </div>
  );

  return (
    <>
      {/*
        The anchor is a span rather than the Icon itself: semantic-ui's Icon
        does not forward a ref to its DOM node, and this needs a real element
        to measure.
      */}
      <span
        ref={anchorRef}
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? idRef.current : undefined}
        onPointerEnter={event => canHover(event) && setOpen(true)}
        onPointerLeave={event => canHover(event) && setOpen(false)}
        onClick={() => setOpen(previous => !previous)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen(previous => !previous);
          }
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'pointer',
          marginLeft: 4,
          ...style,
        }}
      >
        <Icon name="question circle outline" style={{ margin: 0 }} />
      </span>
      {bubble && ReactDOM.createPortal(bubble, document.body)}
    </>
  );
};

export default HoverHelp;
